import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { formatDMY } from "@/lib/dateFormat";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Handshake, Copy, ExternalLink, MessageCircle, Mail, DollarSign, Users, TrendingUp, Share2, Image as ImageIcon, Download, Eye, Landmark, Pencil, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogHeader, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function AffiliatePortal() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [leads, setLeads] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [pform, setPform] = useState({});

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  useEffect(() => {
    (async () => {
      try {
        const [me, ldR, cmR, rsR] = await Promise.all([
          api.get("/affiliate-portal/me"),
          api.get("/affiliate-portal/leads"),
          api.get("/affiliate-portal/commissions"),
          api.get("/affiliate-portal/resources"),
        ]);
        setData(me.data);
        setLeads(ldR.data);
        setCommissions(cmR.data);
        setResources(rsR.data);
        if (!me.data?.affiliate?.profile_completed && !localStorage.getItem("aff_onb_shown")) {
          localStorage.setItem("aff_onb_shown", "1");
          openProfileDialog(me.data.affiliate);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "No se pudo cargar tu portal");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado`);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const downloadImage = async (r) => {
    setDownloading(r.id);
    try {
      const res = await api.get(r.image_download_url.replace(/^\/api/, ""), { responseType: "blob" });
      const ext = (res.data.type || "image/png").split("/")[1]?.split("+")[0] || "png";
      const slug = (r.title || "diseno")
        .toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "diseno";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug}-wespeakspanish.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Imagen descargada");
    } catch {
      toast.error("No se pudo descargar la imagen");
    } finally {
      setDownloading(null);
    }
  };

  const openProfileDialog = (a) => {
    setPform({
      whatsapp: a.whatsapp || "",
      payout_iban: a.payout_iban || "",
      payout_bank: a.payout_bank || "",
      payout_holder: a.payout_holder || "",
      payout_holder_id: a.payout_holder_id || "",
      payout_sinpe: a.payout_sinpe || "",
      promo_plan: a.promo_plan || "",
      promo_urls: a.promo_urls || "",
    });
    setProfileOpen(true);
  };

  const saveProfile = async () => {
    if (!pform.payout_iban.trim() && !pform.payout_sinpe.trim()) {
      toast.error("Ingresá al menos tu IBAN o tu número SINPE Móvil");
      return;
    }
    setSavingProfile(true);
    try {
      const r = await api.patch("/affiliate-portal/me", pform);
      setData((d) => ({ ...d, affiliate: { ...d.affiliate, ...r.data } }));
      setProfileOpen(false);
      toast.success("Información guardada, ¡gracias!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSavingProfile(false);
    }
  };

  const shareImage = async (r) => {
    const text = (r.title ? r.title + " — " : "") + r.share_url;
    try {
      const res = await api.get(r.image_download_url.replace(/^\/api/, ""), { responseType: "blob" });
      const type = res.data.type || "image/png";
      const ext = type.split("/")[1]?.split("+")[0] || "png";
      const file = new File([res.data], `wespeakspanish.${ext}`, { type });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text, title: r.title || "We Speak Spanish CR" });
        return;
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  if (loading) return <div className="p-8 text-slate-500">Cargando tu portal…</div>;
  if (!data) return <div className="p-8 text-slate-500">No se encontró tu registro de afiliado.</div>;

  const { affiliate, stats, marketing_kit: kit } = data;
  const statusStyles = {
    new: "bg-slate-100 text-slate-700",
    contacted: "bg-amber-100 text-amber-700",
    introductory_class: "bg-blue-100 text-blue-700",
    interview_meeting: "bg-purple-100 text-purple-700",
    send_info: "bg-indigo-100 text-indigo-700",
    info_ready: "bg-cyan-100 text-cyan-700",
    enrolled: "bg-emerald-100 text-emerald-700",
    later: "bg-sky-100 text-sky-700",
    not_interested: "bg-red-100 text-red-700",
  };

  return (
    <div className="space-y-6 pb-8" data-testid="affiliate-portal">
      <PageHeader
        title={`¡Hola, ${affiliate.name.split(" ")[0]}!`}
        subtitle={`Tu código: `}
      />
      <div className="-mt-4 flex items-center gap-2">
        <Badge className="font-mono text-sm bg-brand-blue/10 text-brand-blue border-brand-blue/20 px-3 py-1">
          <Handshake className="h-3.5 w-3.5 mr-1.5 inline" />
          {affiliate.code}
        </Badge>
        <Badge variant="outline">{affiliate.commission_pct}% por venta</Badge>
      </div>

      {/* Banner: perfil incompleto */}
      {!affiliate.profile_completed && (
        <Card className="p-4 border-amber-300 bg-amber-50 flex items-start gap-3 flex-wrap" data-testid="profile-incomplete-banner">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-[220px]">
            <div className="font-medium text-amber-900 text-sm">Completá tu información para recibir tus comisiones</div>
            <div className="text-xs text-amber-800 mt-0.5">Necesitamos tu cuenta de depósito (IBAN o SINPE Móvil) y algunos datos básicos.</div>
          </div>
          <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => openProfileDialog(affiliate)} data-testid="complete-profile-btn">
            Completar ahora
          </Button>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="p-4 border-l-4 border-l-amber-500">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Comisiones pendientes</div>
              <div className="text-2xl font-bold text-amber-600 mt-1" data-testid="portal-pending">${stats.pending_total.toFixed(2)}</div>
            </div>
            <DollarSign className="h-8 w-8 text-amber-500 opacity-60" />
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-l-emerald-500">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Comisiones pagadas</div>
              <div className="text-2xl font-bold text-emerald-600 mt-1" data-testid="portal-paid">${stats.paid_total.toFixed(2)}</div>
            </div>
            <TrendingUp className="h-8 w-8 text-emerald-500 opacity-60" />
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-l-brand-blue">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Este mes</div>
              <div className="text-2xl font-bold text-brand-blue mt-1" data-testid="portal-month">${stats.this_month_total.toFixed(2)}</div>
            </div>
            <Users className="h-8 w-8 text-brand-blue opacity-60" />
          </div>
        </Card>
      </div>

      {/* Mi información (pago y promoción) */}
      <Card className="p-5" data-testid="my-info-card">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Landmark className="h-5 w-5 text-brand-blue" /> Mi información
          </h2>
          <Button size="sm" variant="outline" onClick={() => openProfileDialog(affiliate)} data-testid="edit-my-info-btn">
            <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
          </Button>
        </div>
        {affiliate.profile_completed ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {affiliate.whatsapp && (<div><span className="text-slate-500">WhatsApp: </span><span className="text-slate-900">{affiliate.whatsapp}</span></div>)}
            {affiliate.payout_iban && (<div><span className="text-slate-500">IBAN: </span><span className="text-slate-900 font-mono text-xs">{affiliate.payout_iban}</span></div>)}
            {affiliate.payout_bank && (<div><span className="text-slate-500">Banco: </span><span className="text-slate-900">{affiliate.payout_bank}</span></div>)}
            {affiliate.payout_holder && (<div><span className="text-slate-500">Titular: </span><span className="text-slate-900">{affiliate.payout_holder}{affiliate.payout_holder_id ? ` (${affiliate.payout_holder_id})` : ""}</span></div>)}
            {affiliate.payout_sinpe && (<div><span className="text-slate-500">SINPE Móvil: </span><span className="text-slate-900">{affiliate.payout_sinpe}</span></div>)}
            {affiliate.promo_plan && (<div className="sm:col-span-2"><span className="text-slate-500">Cómo promociono: </span><span className="text-slate-900">{affiliate.promo_plan}</span></div>)}
            {affiliate.promo_urls && (<div className="sm:col-span-2"><span className="text-slate-500">Dónde publico: </span><span className="text-slate-900">{affiliate.promo_urls}</span></div>)}
          </div>
        ) : (
          <p className="text-sm text-slate-500">Aún no registraste tu cuenta de depósito. Completala para poder recibir tus comisiones.</p>
        )}
      </Card>

      {/* Marketing kit */}
      <Card className="p-5" data-testid="marketing-kit">
        <div className="flex items-center gap-2 mb-3">
          <Share2 className="h-5 w-5 text-brand-blue" />
          <h2 className="text-lg font-semibold text-slate-900">Kit para compartir</h2>
        </div>
        <p className="text-sm text-slate-600 mb-4">Compartí tu link con familia, amigos y redes. Todos los que se registren con él quedan asociados a vos y generan tu comisión al pagar.</p>

        {/* Referral URL */}
        <div className="mb-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Tu link personal</div>
          <div className="flex items-center gap-2 p-2 bg-slate-50 rounded border border-slate-200">
            <code className="flex-1 text-sm text-slate-700 font-mono truncate" data-testid="portal-referral-url">{kit.referral_url}</code>
            <Button size="sm" variant="outline" onClick={() => copy(kit.referral_url, "Link")} data-testid="copy-referral-url">
              <Copy className="h-3.5 w-3.5 mr-1" /> Copiar
            </Button>
            <a href={kit.referral_url} target="_blank" rel="noopener noreferrer">
              <Button size="sm" variant="ghost"><ExternalLink className="h-3.5 w-3.5" /></Button>
            </a>
          </div>
        </div>

        {/* WhatsApp share */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1"><MessageCircle className="h-3 w-3" /> Mensaje para WhatsApp</div>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={() => copy(kit.whatsapp_message, "Mensaje")} data-testid="copy-whatsapp-msg">
                <Copy className="h-3.5 w-3.5 mr-1" /> Copiar
              </Button>
              <a href={kit.share_whatsapp_href} target="_blank" rel="noopener noreferrer">
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="share-whatsapp-btn">
                  <MessageCircle className="h-3.5 w-3.5 mr-1" /> Abrir en WhatsApp
                </Button>
              </a>
            </div>
          </div>
          <textarea
            readOnly
            value={kit.whatsapp_message}
            className="w-full p-3 border border-slate-200 rounded bg-slate-50 text-sm text-slate-700 font-sans resize-none"
            rows={6}
            data-testid="portal-whatsapp-text"
          />
        </div>

        {/* Email share */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1"><Mail className="h-3 w-3" /> Plantilla de correo</div>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={() => copy(kit.email_subject, "Asunto")} data-testid="copy-email-subject">Copiar asunto</Button>
              <Button size="sm" variant="outline" onClick={() => copy(kit.email_body_text, "Cuerpo")} data-testid="copy-email-body">Copiar cuerpo</Button>
            </div>
          </div>
          <div className="p-3 border border-slate-200 rounded bg-slate-50 text-sm">
            <div className="text-slate-500 text-xs">Asunto:</div>
            <div className="text-slate-900 font-medium mb-2">{kit.email_subject}</div>
            <div className="text-slate-500 text-xs">Cuerpo:</div>
            <div className="text-slate-700 whitespace-pre-line" dangerouslySetInnerHTML={{ __html: kit.email_body_html }} />
          </div>
        </div>

        {/* Hashtags */}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Hashtags sugeridos</div>
          <div className="flex flex-wrap gap-1.5">
            {kit.hashtags.map((h) => (
              <button
                key={h}
                type="button"
                onClick={() => copy(h, h)}
                className="text-xs px-2 py-1 rounded-full bg-brand-blue/10 text-brand-blue border border-brand-blue/20 hover:bg-brand-blue/20 transition-colors"
              >
                {h}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Páginas para compartir (imágenes) */}
      {resources.length > 0 && (
        <Card className="p-5" data-testid="portal-resources">
          <h2 className="text-lg font-semibold text-slate-900 mb-2 flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-brand-blue" /> Páginas para compartir
          </h2>
          <p className="text-sm text-slate-600 mb-4">
            Cada imagen redirige a una página específica del sitio con tu código de afiliado
            incluido, así podés rastrear cuánta gente entra desde vos.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {resources.map((r) => (
              <div key={r.id} className="rounded-md border border-slate-200 overflow-hidden bg-white" data-testid={`portal-resource-${r.id}`}>
                <button
                  type="button"
                  onClick={() => setPreview(r)}
                  className="block relative aspect-video bg-slate-100 group w-full cursor-zoom-in"
                  data-testid={`resource-preview-${r.id}`}
                  title="Ver diseño completo"
                >
                  <img
                    src={`${backendUrl}${r.image_view_url}`}
                    alt={r.title || "Recurso"}
                    className="absolute inset-0 h-full w-full object-cover transition-transform group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
                    <Eye className="h-7 w-7 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" />
                  </div>
                </button>
                <div className="p-3 space-y-2">
                  {r.title && <div className="font-medium text-slate-900 text-sm truncate">{r.title}</div>}
                  <div className="text-[11px] text-slate-500 truncate font-mono" title={r.share_url}>{r.share_url}</div>
                  <div className="flex gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => copy(r.share_url, "Link")}
                      data-testid={`resource-copy-${r.id}`}
                    >
                      <Copy className="h-3.5 w-3.5 mr-1" /> Copiar mi link
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadImage(r)}
                      disabled={downloading === r.id}
                      title="Descargar imagen"
                      data-testid={`resource-download-${r.id}`}
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent((r.title ? r.title + " — " : "") + r.share_url)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => { e.preventDefault(); shareImage(r); }}
                    >
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        title="Compartir por WhatsApp"
                        data-testid={`resource-wa-${r.id}`}
                      >
                        <MessageCircle className="h-3.5 w-3.5" />
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Formulario de información del afiliado */}
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="profile-dialog">
          <DialogHeader>
            <DialogTitle>Mi información</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">
            Usamos estos datos para depositar tus comisiones y coordinar contigo. Podés editarlos cuando quieras.
          </p>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">WhatsApp</Label>
              <Input value={pform.whatsapp || ""} onChange={(e) => setPform({ ...pform, whatsapp: e.target.value })} placeholder="+506 8888 8888" data-testid="pform-whatsapp" />
            </div>
            <div className="pt-1 border-t border-slate-100">
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mt-2 mb-1">Cuenta para depósito de comisiones</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label className="text-xs">Cuenta IBAN (Costa Rica)</Label>
                  <Input value={pform.payout_iban || ""} onChange={(e) => setPform({ ...pform, payout_iban: e.target.value.toUpperCase() })} placeholder="CR05 0152 0200 1026 2840 66" className="font-mono" data-testid="pform-iban" />
                </div>
                <div>
                  <Label className="text-xs">Banco</Label>
                  <Input value={pform.payout_bank || ""} onChange={(e) => setPform({ ...pform, payout_bank: e.target.value })} placeholder="BAC, BCR, BN…" data-testid="pform-bank" />
                </div>
                <div>
                  <Label className="text-xs">SINPE Móvil (alternativa)</Label>
                  <Input value={pform.payout_sinpe || ""} onChange={(e) => setPform({ ...pform, payout_sinpe: e.target.value })} placeholder="8888 8888" data-testid="pform-sinpe" />
                </div>
                <div>
                  <Label className="text-xs">Titular de la cuenta</Label>
                  <Input value={pform.payout_holder || ""} onChange={(e) => setPform({ ...pform, payout_holder: e.target.value })} placeholder="Nombre completo" data-testid="pform-holder" />
                </div>
                <div>
                  <Label className="text-xs">Cédula / ID del titular</Label>
                  <Input value={pform.payout_holder_id || ""} onChange={(e) => setPform({ ...pform, payout_holder_id: e.target.value })} placeholder="1-1234-5678" data-testid="pform-holder-id" />
                </div>
              </div>
            </div>
            <div className="pt-1 border-t border-slate-100">
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mt-2 mb-1">¿Cómo planeás promocionar?</div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Contanos tu plan (redes, boca a boca, comunidad…)</Label>
                  <Textarea rows={2} value={pform.promo_plan || ""} onChange={(e) => setPform({ ...pform, promo_plan: e.target.value })} placeholder="Ej: grupos de expats en Facebook y mi cuenta de Instagram" data-testid="pform-promo-plan" />
                </div>
                <div>
                  <Label className="text-xs">Website o grupo de WhatsApp donde planeás publicar</Label>
                  <Input value={pform.promo_urls || ""} onChange={(e) => setPform({ ...pform, promo_urls: e.target.value })} placeholder="https://miblog.com, link del grupo…" data-testid="pform-promo-urls" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setProfileOpen(false)} data-testid="pform-later-btn">Completar después</Button>
            <Button onClick={saveProfile} disabled={savingProfile} className="btn-brand" data-testid="pform-save-btn">
              {savingProfile ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lightbox de vista previa */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-slate-950 border-slate-800" data-testid="resource-lightbox">
          <DialogTitle className="sr-only">{preview?.title || "Vista previa del diseño"}</DialogTitle>
          {preview && (
            <div>
              <img
                src={`${backendUrl}${preview.image_view_url}`}
                alt={preview.title || "Diseño"}
                className="w-full max-h-[70vh] object-contain bg-slate-950"
              />
              <div className="p-4 flex flex-wrap items-center gap-2 bg-slate-900">
                <div className="flex-1 min-w-[180px]">
                  {preview.title && <div className="text-white font-medium text-sm">{preview.title}</div>}
                  <div className="text-[11px] text-slate-400 truncate font-mono" title={preview.share_url}>{preview.share_url}</div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="bg-transparent border-slate-600 text-white hover:bg-slate-800 hover:text-white"
                  onClick={() => copy(preview.share_url, "Link")}
                  data-testid="lightbox-copy-link"
                >
                  <Copy className="h-3.5 w-3.5 mr-1" /> Copiar mi link
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="bg-transparent border-slate-600 text-white hover:bg-slate-800 hover:text-white"
                  onClick={() => downloadImage(preview)}
                  disabled={downloading === preview.id}
                  data-testid="lightbox-download"
                >
                  <Download className="h-3.5 w-3.5 mr-1" /> Descargar
                </Button>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent((preview.title ? preview.title + " — " : "") + preview.share_url)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => { e.preventDefault(); shareImage(preview); }}
                >
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="lightbox-wa">
                    <MessageCircle className="h-3.5 w-3.5 mr-1" /> Compartir
                  </Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* My leads */}
      <Card className="p-5" data-testid="portal-leads">
        <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Users className="h-5 w-5 text-brand-blue" /> Mis referidos ({leads.length})
        </h2>
        {leads.length === 0 ? (
          <p className="text-sm text-slate-500">Aún no tenés referidos. Compartí tu link arriba para empezar 🚀</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-slate-500">
                <tr>
                  <th className="text-left py-2 font-medium">Fecha</th>
                  <th className="text-left py-2 font-medium">Nombre</th>
                  <th className="text-left py-2 font-medium">Programa</th>
                  <th className="text-left py-2 font-medium">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {leads.map((l) => (
                  <tr key={l.id}>
                    <td className="py-2 text-slate-700">{formatDMY((l.created_at || "").slice(0, 10), lang)}</td>
                    <td className="py-2 text-slate-900">{l.display_name}</td>
                    <td className="py-2 text-slate-600">{l.group || "—"}</td>
                    <td className="py-2">
                      <Badge className={statusStyles[l.status] || "bg-slate-100 text-slate-700"}>
                        {t(`lead_status_${l.status}`) || l.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* My commissions */}
      <Card className="p-5" data-testid="portal-commissions">
        <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-brand-blue" /> Mis comisiones ({commissions.length})
        </h2>
        {commissions.length === 0 ? (
          <p className="text-sm text-slate-500">Aún no hay comisiones. Se generan automáticamente cuando alguno de tus referidos paga su primer ciclo.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-slate-500">
                <tr>
                  <th className="text-left py-2 font-medium">Fecha</th>
                  <th className="text-left py-2 font-medium">Estudiante</th>
                  <th className="text-left py-2 font-medium">Programa</th>
                  <th className="text-right py-2 font-medium">Comisión</th>
                  <th className="text-center py-2 font-medium">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {commissions.map((c) => (
                  <tr key={c.id}>
                    <td className="py-2 text-slate-700">{formatDMY((c.created_at || "").slice(0, 10), lang)}</td>
                    <td className="py-2 text-slate-900">{c.student_name || "—"}</td>
                    <td className="py-2 text-slate-600">{c.program_name || "—"}</td>
                    <td className="py-2 text-right font-mono font-semibold text-slate-900">${c.commission_amount.toFixed(2)}</td>
                    <td className="py-2 text-center">
                      {c.status === "pending" && <Badge className="bg-amber-100 text-amber-700 border-amber-200">Pendiente</Badge>}
                      {c.status === "paid" && (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                          Pagada{c.paid_at ? ` · ${formatDMY(c.paid_at, lang)}` : ""}
                        </Badge>
                      )}
                      {c.status === "cancelled" && <Badge className="bg-slate-100 text-slate-500 border-slate-200">Cancelada</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
